import React, { useEffect, useRef } from 'react';
export type Backdrop = 'shimmer' | 'embers' | 'none';
interface AmbientCanvasProps {
  /** Which ambient field to render. */
  mode: Exclude<Backdrop, 'none'>;
  /** Line/particle color as an "r g b" triple, e.g. "58 74 51". */
  tint: string;
}
interface Ember {
  x: number;
  y: number;
  r: number;
  vy: number;
  drift: number;
  phase: number;
  life: number;
}

/**
 * Pointer-reactive ambient field behind the page.
 * "shimmer" = hairline heat waves that bend away from the cursor.
 * "embers"  = warm motes rising off an unseen flame, nudged by the cursor.
 */
export function AmbientCanvas({
  mode,
  tint
}: AmbientCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let width = 0;
    let height = 0;
    let raf = 0;
    let embers: Ember[] = [];
    const pointer = {
      x: -9999,
      y: -9999,
      strength: 0,
      target: 0
    };
    const seedEmbers = () => {
      const count = Math.min(90, Math.round(width * height / 16000));
      embers = Array.from({
        length: count
      }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: 0.7 + Math.random() * 1.9,
        vy: 0.16 + Math.random() * 0.42,
        drift: 0.25 + Math.random() * 0.7,
        phase: Math.random() * Math.PI * 2,
        life: Math.random()
      }));
    };
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (mode === 'embers') seedEmbers();
    };
    const drawShimmer = (t: number) => {
      ctx.clearRect(0, 0, width, height);
      const rows = height > 620 ? 22 : 15;
      const step = Math.max(6, width / 160);
      for (let i = 0; i < rows; i += 1) {
        const p = i / (rows - 1);
        const baseY = height * 0.06 + p * height * 0.88;
        const amp = 8 + Math.sin(p * Math.PI) * 20;
        ctx.beginPath();
        for (let x = -step; x <= width + step; x += step) {
          const nx = x / Math.max(width, 1);
          const dx = x - pointer.x;
          const dy = baseY - pointer.y;
          const d2 = dx * dx + dy * dy;
          const pull = Math.exp(-d2 / 34000) * 54 * pointer.strength;
          const y = baseY + Math.sin(nx * 3.2 + t * 0.00028 + i * 0.42) * amp + Math.sin(nx * 8.1 - t * 0.00019 + i * 0.83) * (amp * 0.28) - pull;
          if (x <= 0) ctx.moveTo(x, y);else ctx.lineTo(x, y);
        }
        const alpha = 0.03 + Math.sin(p * Math.PI) * 0.075;
        ctx.strokeStyle = `rgb(${tint} / ${alpha.toFixed(3)})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    };
    const drawEmbers = (t: number) => {
      ctx.clearRect(0, 0, width, height);
      for (const e of embers) {
        if (!reduce) {
          e.y -= e.vy;
          e.x += Math.sin(t * 0.0008 + e.phase) * e.drift * 0.5;
          const dx = e.x - pointer.x;
          const dy = e.y - pointer.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 26000) {
            const push = (1 - d2 / 26000) * 1.5 * pointer.strength;
            e.x += dx / (Math.sqrt(d2) || 1) * push * 2;
            e.y += dy / (Math.sqrt(d2) || 1) * push;
          }
          if (e.y < -12) {
            e.y = height + 12;
            e.x = Math.random() * width;
          }
        }
        const fade = 0.1 + 0.5 * (e.y / Math.max(height, 1));
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgb(${tint} / ${(fade * 0.4).toFixed(3)})`;
        ctx.fill();
      }
    };
    const frame = (t: number) => {
      pointer.strength += (pointer.target - pointer.strength) * 0.06;
      if (mode === 'shimmer') drawShimmer(t);else drawEmbers(t);
      raf = window.requestAnimationFrame(frame);
    };
    const onPointerMove = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      pointer.target = 1;
    };
    const onPointerLeave = () => {
      pointer.target = 0;
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    if (reduce) {
      if (mode === 'shimmer') drawShimmer(0);else drawEmbers(0);
    } else {
      window.addEventListener('pointermove', onPointerMove, {
        passive: true
      });
      window.addEventListener('pointerleave', onPointerLeave);
      raf = window.requestAnimationFrame(frame);
    }
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [mode, tint]);
  return <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" data-id="element-1" data-mp="2zliknh4lw3c7" />;
}